import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight, UserPlus, Shield, Cpu, Database, CheckCircle } from 'lucide-react';

const NEXT_TARGETS = {
  onboarding: 'https://onboarding.clara360.de',
  document: 'https://onboarding.clara360.de',
  whatsapp: 'https://wa.me/4917097826760',
};

const trustPillars = [
  {
    icon: Shield,
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/15',
    glow: 'rgba(99,102,241,0.25)',
    title: 'Keine Aktion ohne Freigabe',
    text: 'Clara analysiert, strukturiert und empfiehlt. Sie entscheiden, bevor Daten übernommen, geändert oder weiterverarbeitet werden.',
  },
  {
    icon: Cpu,
    color: 'text-violet-400',
    bg: 'bg-violet-500/15',
    glow: 'rgba(139,92,246,0.25)',
    title: 'Modelloffen',
    text: 'Clara360 ist nicht an ein einzelnes KI-Modell gebunden. Lokale, hybride oder cloudbasierte Verarbeitung kann kontrolliert eingesetzt werden.',
  },
  {
    icon: Database,
    color: 'text-blue-400',
    bg: 'bg-blue-500/15',
    glow: 'rgba(59,130,246,0.25)',
    title: 'Daten kontrolliert',
    text: 'Sensible Verwaltungsdaten werden nach Sensibilität behandelt, reduziert, kontextualisiert oder bevorzugt lokal verarbeitet.',
  },
];

const RegisterPage = () => {
  const { signUp, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '' });
  const [registerError, setRegisterError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [hoveredPillar, setHoveredPillar] = useState(null);

  // next-Parameter aus URL auslesen (kein useSearchParams nötig)
  const searchParams = new URLSearchParams(window.location.search);
  const nextParam = searchParams.get('next') || 'onboarding';
  const redirectTarget = NEXT_TARGETS[nextParam] || NEXT_TARGETS.onboarding;

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRegisterError('');
    setSuccessMessage('');

    if (formData.password !== formData.confirmPassword) {
      setRegisterError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (formData.password.length < 8) {
      setRegisterError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    setIsRegistering(true);
    try {
      const result = await signUp(formData.email, formData.password);

      if (!result.success) {
        setRegisterError(result.error || 'Registrierung fehlgeschlagen.');
      } else if (result.needsEmailConfirmation) {
        // E-Mail-Bestätigung erforderlich
        setSuccessMessage('Bitte bestätigen Sie Ihre E-Mail-Adresse. Danach können Sie Ihren Clara360-Import starten.');
      } else {
        // Session direkt aktiv
        localStorage.setItem('clara_has_account', 'true');
        window.location.href = redirectTarget;
      }
    } catch {
      setRegisterError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #312e81 70%, #1e1b4b 100%)' }}
    >
      {/* Hintergrund-Blur-Orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/6 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/6 w-80 h-80 bg-violet-600/10 rounded-full blur-3xl" />
        <div className="absolute top-2/3 left-1/2 w-64 h-64 bg-blue-600/8 rounded-full blur-3xl" />
      </div>

      {/* Logo */}
      <div className="relative z-10 mb-8 flex items-center gap-3">
        <img src="/clara360-logo.png" alt="Clara360" className="h-9 w-auto" />
        <span className="text-white font-semibold text-lg tracking-tight">Clara360</span>
      </div>

      {/* Mobile Trust-Chips (nur auf Mobile sichtbar) */}
      <div className="relative z-10 md:hidden flex flex-wrap gap-2 justify-center mb-6 px-2">
        {trustPillars.map(({ title }) => (
          <span
            key={title}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-gray-300"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
          >
            <CheckCircle className="w-3 h-3 text-indigo-400 flex-shrink-0" />
            {title}
          </span>
        ))}
      </div>

      {/* Hauptbereich — zweispaltig auf Desktop */}
      <div className="relative z-10 w-full max-w-4xl flex flex-col md:flex-row gap-6 md:gap-8 items-start">

        {/* Linke Spalte — Trust-Karten (nur Desktop) */}
        <div className="hidden md:flex flex-col gap-4 flex-1 pt-2">
          <div className="mb-2">
            <h2 className="text-white font-semibold text-xl leading-tight mb-2">
              Clara360 ist nicht nur intelligent —
            </h2>
            <p className="text-indigo-300 font-medium text-base">Clara360 ist kontrollierbar.</p>
          </div>

          {trustPillars.map(({ icon: Icon, color, bg, glow, title, text }, i) => (
            <div
              key={title}
              onMouseEnter={() => setHoveredPillar(i)}
              onMouseLeave={() => setHoveredPillar(null)}
              className="flex items-start gap-4 p-4 rounded-xl cursor-default transition-all duration-300"
              style={{
                background: hoveredPillar === i ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${hoveredPillar === i ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.07)'}`,
                boxShadow: hoveredPillar === i ? `0 0 24px ${glow}` : 'none',
              }}
            >
              <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm mb-1">{title}</h3>
                <p className="text-gray-400 text-xs leading-relaxed">{text}</p>
              </div>
            </div>
          ))}

          <p className="text-gray-600 text-xs mt-2 leading-relaxed">
            Lokal bevorzugt · Cloud kontrolliert · Modelloffen
          </p>
        </div>

        {/* Rechte Spalte — Formular */}
        <div
          className="w-full md:w-[420px] flex-shrink-0 rounded-2xl p-7"
          style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow: '0 8px 48px rgba(99,102,241,0.18)',
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-indigo-500/20">
              <UserPlus className="h-5 w-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-lg leading-tight">Pilotzugang erstellen</h2>
              <p className="text-gray-400 text-xs">Kostenlos anfragen · Geführter Pilotbetrieb</p>
            </div>
          </div>

          {/* Erfolgs-Meldung */}
          {successMessage && (
            <div className="rounded-lg p-4 mb-4 bg-indigo-500/15 border border-indigo-500/30 flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
              <p className="text-indigo-300 text-sm leading-relaxed">{successMessage}</p>
            </div>
          )}

          {/* Prozesszeile */}
          {!successMessage && (
            <div className="flex items-center gap-0 mb-5 text-xs">
              {[['1', 'Account erstellen'], ['2', 'Bedarf einordnen'], ['3', 'Pilotzugang starten']].map(([num, label], i, arr) => (
                <React.Fragment key={num}>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-full bg-indigo-600/60 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold" style={{fontSize:'9px'}}>{num}</span>
                    </div>
                    <span className={i === 0 ? 'text-indigo-300 font-medium' : 'text-gray-500'}>{label}</span>
                  </div>
                  {i < arr.length - 1 && <div className="flex-1 h-px bg-white/10 mx-2" />}
                </React.Fragment>
              ))}
            </div>
          )}

          {!successMessage && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* E-Mail */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">E-Mail</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    type="email" id="email" name="email"
                    value={formData.email} onChange={handleChange} required
                    placeholder="ihre@email.de"
                    className="w-full pl-9 pr-4 py-3 rounded-lg text-sm text-white placeholder-gray-500 border focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                    style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)' }}
                  />
                </div>
              </div>

              {/* Passwort */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">Passwort</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    type={showPassword ? 'text' : 'password'} id="password" name="password"
                    value={formData.password} onChange={handleChange} required
                    placeholder="Mindestens 8 Zeichen"
                    className="w-full pl-9 pr-10 py-3 rounded-lg text-sm text-white placeholder-gray-500 border focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                    style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)' }}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showPassword ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                  </button>
                </div>
              </div>

              {/* Passwort bestätigen */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">Passwort bestätigen</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    type={showConfirm ? 'text' : 'password'} id="confirmPassword" name="confirmPassword"
                    value={formData.confirmPassword} onChange={handleChange} required
                    placeholder="Passwort wiederholen"
                    className="w-full pl-9 pr-10 py-3 rounded-lg text-sm text-white placeholder-gray-500 border focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                    style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)' }}
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showConfirm ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                  </button>
                </div>
              </div>

              {registerError && (
                <div className="rounded-lg p-3 bg-red-500/15 border border-red-500/30">
                  <p className="text-red-400 text-xs">{registerError}</p>
                </div>
              )}

              <button
                type="submit" disabled={isRegistering}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-2"
              >
                {isRegistering ? (
                  <><div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Registrierung läuft...</>
                ) : (
                  <><ArrowRight className="h-4 w-4" />Pilotzugang erstellen</>
                )}
              </button>

              {/* Erwartungserklärung */}
              <p className="text-gray-600 text-xs leading-relaxed pt-1">
                Nach der Registrierung führen wir Sie Schritt für Schritt weiter. Uploads und Datenübernahmen erfolgen erst nach bewusster Freigabe.
              </p>
            </form>
          )}

          <div className="mt-5 pt-4 border-t border-white/8">
            <p className="text-center text-xs text-gray-600">
              Bereits einen Account?{' '}
              <Link to="/login" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                Hier anmelden
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 mt-8 flex items-center gap-2" style={{marginTop: '1.5rem'}}>
        <Shield className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
        <p className="text-gray-600 text-xs text-center">
          Pilotbetrieb · Keine Rechts- oder Steuerberatung · Lokal bevorzugt · Cloud kontrolliert · Keine Aktion ohne Freigabe
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
